import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { ExpandedState } from "@tanstack/react-table";
import { z } from "zod";
import { formatISO } from "date-fns";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { ViewToolbar } from "@components/views/ViewToolbar/ViewToolbar";
import { CounterpartyDirectoryView } from "@common/types/views/counterpartyDirectoryView";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { useBusinessNonMockEntityIds } from "@hooks/useBusinessEntityIds";
import { useScope } from "@state/scopeContext.lib";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { Card } from "@design-system/Card/Card";
import {
    FilterTabs,
    FiltersChangeFn,
} from "@components/pageHeader/filters/lib";
import { glDirectionRadios } from "@components/pageHeader/filters/Tabs/AmountFilter/directionRadios";
import {
    counterpartiesQueryKeys,
    getCounterparties,
} from "@api/counterparty.api";
import styles from "./DirectoryView.module.scss";
import { DirectoryFiltersForm } from "./DirectoryFiltersForm";
import { DirectoryDownload } from "./DirectoryDownload";
import { DirectoryContext } from "./Directory.context";
import { DirectoryTable } from "./DirectoryTable";
import { useCounterpartyDirectoryViewConfig } from "./useCounterpartyDirectoryViewConfig";
import {
    getCounterparty1099ReviewYear,
    useBuiltInCounterpartyDirectoryViews,
} from "./useBuiltInCounterpartyDirectoryViews";
import { CounterpartyDetailsPanel } from "./CounterpartyDetailsPanel";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";

const HEADER_OFFSET_PX = 192;

const baseViewIdCacheSchema = z.string();

const detailsAsideStyle: React.CSSProperties & Record<string, string> = {
    "--directory-offset": `${HEADER_OFFSET_PX}px`,
};

export const DirectoryView: React.FC = () => {
    const {
        filters,
        setFilters,
        counterpartySort,
        setAddingNew,
        view,
        onViewChange,
        selectedRow,
    } = useContext(DirectoryContext);

    const { viewConfig } = useCounterpartyDirectoryViewConfig();
    const builtInViews = useBuiltInCounterpartyDirectoryViews();
    const businessEntityIds = useBusinessNonMockEntityIds();

    const [expanded, setExpanded] = useState<ExpandedState>({});
    const tableRef = useRef<ViewTableHandle>(null);

    // Mirrors the base view id owned by the ViewToolbar (same cache key) so
    // the page knows whether a 1099 review view is active after a reload.
    const [cachedBaseViewId] = useWorkspaceUserCache<string>(
        WORKSPACE_USER_CACHE_KEYS.COUNTERPARTY_DIRECTORY_BASE_VIEW_ID_CACHE_KEY,
        baseViewIdCacheSchema,
    );
    const [baseViewId, setBaseViewId] = useState<string | undefined>(
        cachedBaseViewId,
    );
    const isSingleEntityMode =
        getCounterparty1099ReviewYear(baseViewId) !== undefined;

    // Selecting a 1099 review view scopes the page to the matching calendar
    // year and switches it to single-entity mode, keeping only the first
    // currently selected entity.
    const handleViewSelect = useCallback(
        (selectedView: CounterpartyDirectoryView) => {
            onViewChange(selectedView);

            // Selected views are NamedViews at runtime; the toolbar contract
            // only types them as the bare view shape.
            const viewId =
                "id" in selectedView && typeof selectedView.id === "string"
                    ? selectedView.id
                    : undefined;
            setBaseViewId(viewId);

            const reviewYear = getCounterparty1099ReviewYear(viewId);
            if (reviewYear === undefined) {
                return;
            }

            const firstEntityId =
                filters.entityIds?.[0] ?? businessEntityIds[0];
            setFilters({
                ...filters,
                startDate: formatISO(new Date(reviewYear, 0, 1), {
                    representation: "date",
                }),
                endDate: formatISO(new Date(reviewYear, 11, 31), {
                    representation: "date",
                }),
                entityIds:
                    firstEntityId !== undefined ? [firstEntityId] : undefined,
                // Spec: 1099 review is always cash-basis.
                basis: AccountingLedgerBasis.cash,
            });
        },
        [onViewChange, filters, setFilters, businessEntityIds],
    );

    const coaAccounts = view.filters.coaAccounts;
    const overlayAccountIds = view.filters.accountIds;

    const overlayFilters = useMemo(
        () => ({ ...view.filters, entityIds: filters.entityIds }),
        [view.filters, filters.entityIds],
    );

    const defaultOverlayFilters = useMemo(
        () => ({ entityIds: filters.entityIds }),
        [filters.entityIds],
    );

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (payload) => {
            onViewChange({
                ...view,
                filters: { ...view.filters, ...payload },
            });
        },
        [view, onViewChange],
    );

    const { workspaceId } = useScope();

    const queryKey = useMemo(
        () => [
            ...counterpartiesQueryKeys.query(workspaceId ?? ""),
            filters,
            coaAccounts,
            overlayAccountIds,
            counterpartySort,
            view.filters.minAmount,
            view.filters.maxAmount,
            view.filters.direction,
            view.filters.amountRounded,
            view.filters.amountRoundingLevel,
            view.filters.isEligible1099,
            view.filters.shouldExcludeCreditCardJournalEntries,
            view.filters.shouldExcludePaymentProcessorJournalEntries,
            view.filters.includeExcludedAmounts,
            view.filters.journalEntrySources,
            view.filters.amountFilterLevel,
            view.filters.accountClasses,
            view.filters.createdByKick,
            view.filters.createdByUserIds,
            view.filters.createdBySources,
            view.filters.counterpartyReviewedFields,
            view.filters.counterpartyReviewedFieldsMode,
            view.filters.counterpartyUnreviewedFields,
            view.filters.counterpartyUnreviewedFieldsMode,
            view.filters.counterpartyReviewerIds,
            view.filters.counterpartyReviewerMode,
            view.filters.disabledFilter,
        ],
        [
            workspaceId,
            filters,
            coaAccounts,
            overlayAccountIds,
            counterpartySort,
            view.filters.minAmount,
            view.filters.maxAmount,
            view.filters.direction,
            view.filters.amountRounded,
            view.filters.amountRoundingLevel,
            view.filters.isEligible1099,
            view.filters.shouldExcludeCreditCardJournalEntries,
            view.filters.shouldExcludePaymentProcessorJournalEntries,
            view.filters.includeExcludedAmounts,
            view.filters.journalEntrySources,
            view.filters.amountFilterLevel,
            view.filters.accountClasses,
            view.filters.createdByKick,
            view.filters.createdByUserIds,
            view.filters.createdBySources,
            view.filters.counterpartyReviewedFields,
            view.filters.counterpartyReviewedFieldsMode,
            view.filters.counterpartyUnreviewedFields,
            view.filters.counterpartyUnreviewedFieldsMode,
            view.filters.counterpartyReviewerIds,
            view.filters.counterpartyReviewerMode,
            view.filters.disabledFilter,
        ],
    );

    // Collapsed/expanded state is keyed by counterparty id, so a query change
    // can load a completely different row set. Reset it to keep the row state
    // and the toggle-all label in sync with what is actually rendered.
    useEffect(() => {
        setExpanded({});
    }, [queryKey]);

    const counterparties = useQuery({
        queryKey,
        queryFn: () => {
            if (workspaceId) {
                return getCounterparties(workspaceId, {
                    includeUsageStats: true,
                    search: filters.search,
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                    sort: counterpartySort,
                    entityIds: filters.entityIds,
                    basis: filters.basis,
                    coaAccountIds: coaAccounts,
                    financialAccountIds: overlayAccountIds,
                    minAmount: view.filters.minAmount,
                    maxAmount: view.filters.maxAmount,
                    amountDirection: view.filters.direction,
                    amountRounded: view.filters.amountRounded,
                    amountRoundingLevel: view.filters.amountRoundingLevel,
                    isEligible1099: view.filters.isEligible1099,
                    shouldExcludeCreditCardJournalEntries:
                        view.filters.shouldExcludeCreditCardJournalEntries,
                    shouldExcludePaymentProcessorJournalEntries:
                        view.filters
                            .shouldExcludePaymentProcessorJournalEntries,
                    includeExcludedAmounts: view.filters.includeExcludedAmounts,
                    journalEntrySources: view.filters.journalEntrySources,
                    amountFilterLevel: view.filters.amountFilterLevel,
                    createdByKick: view.filters.createdByKick,
                    createdByUserIds: view.filters.createdByUserIds,
                    createdBySources: view.filters.createdBySources,
                    accountClasses: view.filters.accountClasses,
                    counterpartyReviewedFields:
                        view.filters.counterpartyReviewedFields,
                    counterpartyReviewedFieldsMode:
                        view.filters.counterpartyReviewedFieldsMode,
                    counterpartyUnreviewedFields:
                        view.filters.counterpartyUnreviewedFields,
                    counterpartyUnreviewedFieldsMode:
                        view.filters.counterpartyUnreviewedFieldsMode,
                    counterpartyReviewerIds:
                        view.filters.counterpartyReviewerIds,
                    counterpartyReviewerMode:
                        view.filters.counterpartyReviewerMode,
                    disabledFilter: view.filters.disabledFilter,
                });
            }
        },
    });

    const hasExpandableRows = useMemo(() => {
        const hasExpandableCounterparty =
            counterparties.data?.data.some(
                (row) => (row.usageStats?.accountBreakdown ?? []).length > 1,
            ) ?? false;
        const hasExpandableNoCounterparty =
            (counterparties.data?.noCounterpartyTotals?.accountBreakdown ?? [])
                .length > 1;
        return hasExpandableCounterparty || hasExpandableNoCounterparty;
    }, [counterparties.data]);

    return (
        <>
            <ViewToolbar
                view={view}
                onViewChange={onViewChange}
                onViewSelect={handleViewSelect}
                viewConfig={viewConfig}
                builtInViews={builtInViews}
                defaultFilters={{}}
                baseViewCacheKey={
                    WORKSPACE_USER_CACHE_KEYS.COUNTERPARTY_DIRECTORY_BASE_VIEW_ID_CACHE_KEY
                }
            />
            <TablePageHeader
                search={filters.search}
                isFullPage
                onSearchChange={(value) =>
                    setFilters({ ...filters, search: value })
                }
                filters={overlayFilters}
                defaultFilters={defaultOverlayFilters}
                onFiltersChange={handleFiltersChange}
                directionRadios={glDirectionRadios}
                availableFilters={[
                    FilterTabs.ACCOUNT,
                    FilterTabs.LEDGER,
                    FilterTabs.AMOUNT,
                    FilterTabs.ELIGIBLE_1099,
                    FilterTabs.JOURNAL_ENTRY_SOURCES,
                    FilterTabs.LEDGER_GROUP,
                    FilterTabs.COUNTERPARTY_CREATED_BY,
                    FilterTabs.COUNTERPARTY_REVIEW,
                    FilterTabs.COUNTERPARTY_DISABLED,
                ]}
                showAmountFilterLevel
                hideCoaAccountLastChangedBy
            >
                <DirectoryFiltersForm
                    filters={filters}
                    setFilters={setFilters}
                    singleEntitySelection={isSingleEntityMode}
                />
                <DirectoryDownload />
                {hasExpandableRows && (
                    <CollapseAllButton
                        expanded={expanded}
                        tableRef={tableRef}
                    />
                )}
                <WithHotkey
                    hotKey="alt+n"
                    hotKeyOptions={{ description: "Add counterparty" }}
                >
                    <Button
                        variant="primary"
                        size="md"
                        onClick={() => setAddingNew(true)}
                    >
                        Add counterparty
                    </Button>
                </WithHotkey>
            </TablePageHeader>

            <section className={styles.body}>
                <DirectoryTable
                    counterpartiesData={counterparties.data}
                    isLoading={counterparties.isLoading}
                    totalCounterparties={counterparties.data?.data.length ?? 0}
                    tableRef={tableRef}
                    expanded={expanded}
                    onExpandedChange={setExpanded}
                />
                {selectedRow && !selectedRow.isNoCounterparty && (
                    <Card
                        className={styles.detailsAside}
                        style={detailsAsideStyle}
                        sharpEdges
                    >
                        <CounterpartyDetailsPanel />
                    </Card>
                )}
            </section>
        </>
    );
};
